// Polish Phase 1 (AUDIO-01 / AUDIO-04 / PERF-04) — procedural synthesis
// primitives. No samples, no deps: every SFX is generated from oscillators +
// shaped noise + filters at play time. These primitives are the lego bricks
// the cue bank (cues.ts) composes into the ~26 canon cues.
//
// NODE HYGIENE (PERF-04): every node these helpers create is one-shot and
// self-cleaning. Oscillators / buffer sources are start()ed and stop()ped with
// an explicit end time; an `onended` handler disconnects the node chain so the
// graph never accumulates dead nodes (a classic Web-Audio leak where stopped-
// but-connected sources pin their gain/filter chain in memory). The only
// reused allocation is the white-noise AudioBuffer, cached per AudioContext via
// a WeakMap (buffers are immutable + reusable; buffer *sources* are not).

/** A minimal structural view of the bits of AudioContext we touch, so the
 * engine can inject a mock in jsdom/happy-dom tests (which ship no real
 * AudioContext). The real `AudioContext` satisfies this. */
export interface SynthContext {
  readonly currentTime: number
  readonly sampleRate: number
  createOscillator(): OscillatorNode
  createGain(): GainNode
  createBiquadFilter(): BiquadFilterNode
  createBufferSource(): AudioBufferSourceNode
  createBuffer(
    channels: number,
    length: number,
    sampleRate: number,
  ): AudioBuffer
}

const NOISE_BUFFERS = new WeakMap<object, AudioBuffer>()

/** White-noise buffer (~1s), generated once per context and reused for every
 * noise burst (PERF-04 — no per-cue buffer allocation). */
function getNoiseBuffer(ctx: SynthContext): AudioBuffer {
  const key = ctx as unknown as object
  const cached = NOISE_BUFFERS.get(key)
  if (cached) return cached
  const len = Math.max(1, Math.floor(ctx.sampleRate))
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  // Deterministic-ish LCG so the noise is stable and we never touch
  // Math.random (which the workflow/runtime sandbox forbids in some contexts;
  // also keeps the timbre reproducible across renders).
  let seed = 0x2545f485
  for (let i = 0; i < len; i++) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    data[i] = (seed / 0x3fffffff - 1) * 0.9
  }
  NOISE_BUFFERS.set(key, buffer)
  return buffer
}

export interface ToneOptions {
  /** Start frequency (Hz). */
  readonly freq: number
  /** Oscillator timbre. Default 'sine'. */
  readonly type?: OscillatorType
  /** Cents of detune (for chorus/warmth when layered). */
  readonly detune?: number
  /** Seconds from now() to begin (default 0). */
  readonly delay?: number
  /** Seconds the tone sustains at peak before release (default 0.12). */
  readonly duration?: number
  /** Attack ramp seconds (default 0.005). */
  readonly attack?: number
  /** Release tail seconds (default 0.12). */
  readonly release?: number
  /** Peak linear gain (default 0.3). */
  readonly gain?: number
  /** If set, glide the frequency to this value over attack+duration
   * (exponential) — used for the rising trace-whisper + chirps. */
  readonly glideTo?: number
  /** Optional lowpass cutoff (Hz) to soften the timbre. */
  readonly lowpass?: number
}

/** Schedule a single shaped tone into `dest`. Fire-and-forget; self-cleans. */
export function playTone(
  ctx: SynthContext,
  dest: AudioNode,
  opts: ToneOptions,
): void {
  const now = ctx.currentTime + (opts.delay ?? 0)
  const attack = Math.max(0.001, opts.attack ?? 0.005)
  const duration = Math.max(0, opts.duration ?? 0.12)
  const release = Math.max(0.01, opts.release ?? 0.12)
  const peak = opts.gain ?? 0.3

  const osc = ctx.createOscillator()
  osc.type = opts.type ?? 'sine'
  osc.frequency.setValueAtTime(opts.freq, now)
  if (opts.detune) osc.detune.setValueAtTime(opts.detune, now)
  if (opts.glideTo && opts.glideTo > 0) {
    // exponentialRampToValueAtTime needs a non-zero start; freq already > 0.
    osc.frequency.exponentialRampToValueAtTime(
      opts.glideTo,
      now + attack + duration,
    )
  }

  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), now + attack)
  gain.gain.setValueAtTime(Math.max(0.0002, peak), now + attack + duration)
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    now + attack + duration + release,
  )

  let tail: AudioNode = gain
  let filter: BiquadFilterNode | null = null
  if (opts.lowpass) {
    filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(opts.lowpass, now)
    gain.connect(filter)
    tail = filter
  }

  osc.connect(gain)
  tail.connect(dest)

  const end = now + attack + duration + release
  osc.start(now)
  osc.stop(end)
  osc.onended = (): void => {
    try {
      osc.disconnect()
      gain.disconnect()
      filter?.disconnect()
    } catch {
      // already disconnected — ignore
    }
  }
}

export interface NoiseOptions {
  /** Seconds the burst lasts (default 0.2). */
  readonly duration?: number
  /** Attack ramp seconds (default 0.005). */
  readonly attack?: number
  /** Release tail seconds (default 0.1). */
  readonly release?: number
  /** Peak linear gain (default 0.2). */
  readonly gain?: number
  /** Seconds from now() to begin (default 0). */
  readonly delay?: number
  /** Filter shape over the noise. */
  readonly filter?: {
    readonly type: BiquadFilterType
    readonly freq: number
    /** Sweep the cutoff to this value over the burst (rumble/whoosh). */
    readonly sweepTo?: number
    readonly q?: number
  }
}

/** Schedule a shaped noise burst into `dest` — paper rustle, stone crumble,
 * rumble pads, whooshes, coin texture. Self-cleans on ended. */
export function playNoise(
  ctx: SynthContext,
  dest: AudioNode,
  opts: NoiseOptions,
): void {
  const now = ctx.currentTime + (opts.delay ?? 0)
  const attack = Math.max(0.001, opts.attack ?? 0.005)
  const duration = Math.max(0, opts.duration ?? 0.2)
  const release = Math.max(0.01, opts.release ?? 0.1)
  const peak = opts.gain ?? 0.2

  const src = ctx.createBufferSource()
  src.buffer = getNoiseBuffer(ctx)
  src.loop = true

  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), now + attack)
  gain.gain.setValueAtTime(Math.max(0.0002, peak), now + attack + duration)
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    now + attack + duration + release,
  )

  let head: AudioNode = gain
  let filter: BiquadFilterNode | null = null
  if (opts.filter) {
    filter = ctx.createBiquadFilter()
    filter.type = opts.filter.type
    filter.frequency.setValueAtTime(opts.filter.freq, now)
    if (opts.filter.sweepTo) {
      filter.frequency.exponentialRampToValueAtTime(
        Math.max(20, opts.filter.sweepTo),
        now + attack + duration,
      )
    }
    if (opts.filter.q) filter.Q.setValueAtTime(opts.filter.q, now)
    src.connect(filter)
    filter.connect(gain)
    head = gain
  } else {
    src.connect(gain)
  }
  head.connect(dest)

  const end = now + attack + duration + release
  src.start(now)
  src.stop(end)
  src.onended = (): void => {
    try {
      src.disconnect()
      gain.disconnect()
      filter?.disconnect()
    } catch {
      // already disconnected — ignore
    }
  }
}

/** Layer several tones at once (a chord / organ stack). */
export function playChord(
  ctx: SynthContext,
  dest: AudioNode,
  freqs: ReadonlyArray<number>,
  opts: Omit<ToneOptions, 'freq'>,
): void {
  for (const freq of freqs) playTone(ctx, dest, { ...opts, freq })
}

/** Equal-temperament helper: transpose a base frequency by N semitones. */
export function semitone(freq: number, semitones: number): number {
  return freq * 2 ** (semitones / 12)
}
